import { FIRST_BONUS, MAX_ATTEMPTS, MAX_PLAYERS, WORD_LENGTH, WORD_MS } from "../src/protocol";
import type { Lang } from "../src/languages";
import { LANG } from "./i18n";

// Every visible text lives here, once per language. The `Strings` type is derived from the
// English table, so a key missing in Spanish is a compile error.
//
// Wording: WorkBrawl avoids "game"/"play" vocabulary (some workplace filters block it).
// Say round, room, start, people, title, word.

const en = {
  appName: "GuessBrawl",
  pageTitle: "GuessBrawl — Guess the word of the day",
  tagline: "Guess the word of the day",
  homeIntro: `Find a ${WORD_LENGTH}-letter word in ${MAX_ATTEMPTS} tries, with colored hints after each one. One word a day for everyone, or a room to race your team.`,
  uiLanguageLabel: "Language",
  backToHub: "Back to WorkBrawl",
  privacy: "Privacy",
  terms: "Terms",
  consentText: "We use analytics cookies to learn how WorkBrawl is used. They're off unless you accept.",
  consentAccept: "Accept",
  consentReject: "Reject",
  consentNotice: "We use Google Analytics cookies to understand how WorkBrawl is used and improve it.",
  consentGotIt: "Got it",

  dailyTitle: "Word of the day",
  dailyBody: "The same word for everyone today. Come back tomorrow for a new one.",
  dailyStart: "Guess today's word",
  wordLanguageLabel: "Word language",

  newRoomTitle: "Race your team",
  nameLabel: "Your name",
  namePlaceholder: "e.g. Alex",
  nameInvalid: "Enter a name of 1 to 20 characters.",
  durationLabel: "Round length",
  minutes: (n: number) => `${n} minutes`,
  createRoom: "Create room",
  creatingRoom: "Creating…",
  verifying: "Checking…",
  or: "or",
  codeLabel: "Room code",
  codePlaceholder: "ABCDE",
  codeInvalid: "Room codes have 5 letters or numbers.",
  join: "Join",
  createFailed: "Could not create the room. Try again.",
  joinFailed: "Could not check the room. Try again.",
  turnstileFailed: "The security check failed. Try again.",
  turnstileUnavailable: "The security check is unavailable. Reload the page and try again.",
  turnstileInteract: "Tick the box below to confirm you're a person.",
  errorRoomNotFound: "This room does not exist or has expired.",
  errorRoomFull: `This room is full (${MAX_PLAYERS} people).`,
  invalidRoomLink: "That room link isn't valid.",

  howTitle: "How it works",
  howSteps: [
    { title: "Type a word.", text: `Any real ${WORD_LENGTH}-letter word, then press Enter.` },
    { title: "Read the colors.", text: "Green: right letter, right place. Yellow: the letter is in the word, somewhere else. Grey: not in the word." },
    { title: "Find it in six.", text: `In a room, everyone gets the same word, with up to ${WORD_MS / 1000} seconds for each one: the fewer tries, the more points, and ${FIRST_BONUS} extra for whoever solves it first.` },
  ],
  faqTitle: "Frequently asked questions",
  faq: [
    { q: "Is GuessBrawl free?", a: "Yes. It's free, needs no account or download and runs in any modern browser." },
    { q: "When does the word of the day change?", a: "At midnight Ecuador time (UTC−5), at the same moment for everyone, so a whole team always has the same word." },
    { q: "Do accents matter in Spanish?", a: "No. Type the word without them; Ñ is its own letter." },
    { q: "How many people fit in a room?", a: `Up to ${MAX_PLAYERS} people per room, each on their own device.` },
  ],

  namePromptTitle: "What's your name?",
  namePromptBody: "This is how the others will see you on the scoreboard.",
  enterRoom: "Enter room",

  room: "Room",
  exit: "Leave",
  cancel: "Cancel",
  exitTitle: "Leave the room?",
  exitBody: "You'll go back to the start screen. Come back with the room link to rejoin.",
  scoreboard: "Scoreboard",
  timeLeft: "Time left",
  you: "you",
  offline: "away",
  points: (n: number) => (n === 1 ? "1 point" : `${n} points`),

  inviteTitle: "Invite your team",
  copyLink: "Copy link",
  linkCopied: "Copied!",
  linkCopiedToast: "Link copied",
  copyFailed: "Copy this link:",
  copyFailedShort: "Couldn't copy",
  share: "Share",
  shareText: "Join me on GuessBrawl: race to guess the word!",
  close: "Close",
  qrLabel: (code: string) => `QR code to join room ${code}`,

  lobbyTitle: "Ready when you are",
  lobbyBody: (n: number) => (n === 1 ? "You're the only one here. Share the link, or start on your own." : `${n} people in the room.`),
  startRound: "Start round",
  getReady: "Get ready",
  wordNumber: (n: number) => `Word ${n}`,
  wordLeft: "Time for this word",
  wordTimeUp: "Time's up for this word",
  notAWord: "Not in the word list",
  tooShort: `Type ${WORD_LENGTH} letters`,
  solvedMine: (tries: number) => (tries === 1 ? "Solved at the first try!" : `Solved in ${tries} tries!`),
  outOfTries: "Out of tries",
  waitingOthers: "Waiting for the others…",
  answerWas: (word: string) => `The word was ${word}`,
  firstSolved: (name: string) => `${name} solved it first`,
  resultsTitle: "Time's up!",
  winner: (name: string) => `${name} wins`,
  youWin: "You win!",
  tie: "It's a tie",
  newRound: "New round",
  enter: "Enter",
  backspace: "Delete",
  boardLabel: "Your guesses",
  rowLabel: (n: number, word: string, result: string) => `Try ${n}: ${word}, ${result}`,
  markNames: ["not in the word", "in the word elsewhere", "right place"] as readonly string[],
  highContrast: "High-contrast colors",

  dayHeading: (n: number) => `Word of the day #${n}`,
  dayWon: "Well done!",
  dayLost: "Better luck tomorrow",
  shareResult: "Share result",
  resultCopied: "Result copied",
  nextWord: "Next word in",
  stats: "Statistics",
  statPlayed: "Days",
  statWinRate: "Solved %",
  statStreak: "Current streak",
  statBest: "Best streak",
  statDistribution: "Tries",
  backToStart: "Back to start",

  connecting: "Connecting…",
  reconnecting: "Connection lost. Reconnecting…",
  backHome: "Back to start",
  networkError: "Couldn't reach the server. Try again.",
};

export type Strings = typeof en;

const es: Strings = {
  appName: "GuessBrawl",
  pageTitle: "GuessBrawl — Adivina la palabra del día",
  tagline: "Adivina la palabra del día",
  homeIntro: `Encuentra una palabra de ${WORD_LENGTH} letras en ${MAX_ATTEMPTS} intentos, con pistas de colores después de cada uno. Una palabra al día para todos, o una sala para competir con tu equipo.`,
  uiLanguageLabel: "Idioma",
  backToHub: "Volver a WorkBrawl",
  privacy: "Privacidad",
  terms: "Términos",
  consentText: "Usamos cookies de analítica para saber cómo se usa WorkBrawl. Están desactivadas salvo que las aceptes.",
  consentAccept: "Aceptar",
  consentReject: "Rechazar",
  consentNotice: "Usamos cookies de Google Analytics para entender cómo se usa WorkBrawl y mejorarlo.",
  consentGotIt: "Entendido",

  dailyTitle: "Palabra del día",
  dailyBody: "La misma palabra para todos hoy. Vuelve mañana por una nueva.",
  dailyStart: "Adivinar la palabra de hoy",
  wordLanguageLabel: "Idioma de la palabra",

  newRoomTitle: "Compite con tu equipo",
  nameLabel: "Tu nombre",
  namePlaceholder: "p. ej. Álex",
  nameInvalid: "Escribe un nombre de 1 a 20 caracteres.",
  durationLabel: "Duración de la ronda",
  minutes: (n: number) => `${n} minutos`,
  createRoom: "Crear sala",
  creatingRoom: "Creando…",
  verifying: "Verificando…",
  or: "o",
  codeLabel: "Código de sala",
  codePlaceholder: "ABCDE",
  codeInvalid: "Los códigos de sala tienen 5 letras o números.",
  join: "Entrar",
  createFailed: "No se pudo crear la sala. Inténtalo de nuevo.",
  joinFailed: "No se pudo comprobar la sala. Inténtalo de nuevo.",
  turnstileFailed: "La verificación de seguridad falló. Inténtalo de nuevo.",
  turnstileUnavailable: "La verificación de seguridad no está disponible. Recarga la página e inténtalo de nuevo.",
  turnstileInteract: "Marca la casilla de abajo para confirmar que eres una persona.",
  errorRoomNotFound: "Esta sala no existe o ya expiró.",
  errorRoomFull: `Esta sala está llena (${MAX_PLAYERS} personas).`,
  invalidRoomLink: "El enlace de la sala no es válido.",

  howTitle: "Cómo funciona",
  howSteps: [
    { title: "Escribe una palabra.", text: `Cualquier palabra real de ${WORD_LENGTH} letras, y pulsa Enter.` },
    { title: "Lee los colores.", text: "Verde: letra correcta en su sitio. Amarillo: la letra está en la palabra, en otro sitio. Gris: no está." },
    { title: "Encuéntrala en seis.", text: `En una sala, todos tienen la misma palabra, con hasta ${WORD_MS / 1000} segundos para cada una: menos intentos dan más puntos, y ${FIRST_BONUS} extra para quien la resuelva primero.` },
  ],
  faqTitle: "Preguntas frecuentes",
  faq: [
    { q: "¿GuessBrawl es gratis?", a: "Sí. Es gratis, no necesita cuenta ni descarga y funciona en cualquier navegador moderno." },
    { q: "¿Cuándo cambia la palabra del día?", a: "A medianoche hora de Ecuador (UTC−5), al mismo tiempo para todos, así un equipo entero siempre tiene la misma palabra." },
    { q: "¿Importan las tildes?", a: "No. Escribe la palabra sin tildes; la Ñ es una letra propia." },
    { q: "¿Cuántas personas caben en una sala?", a: `Hasta ${MAX_PLAYERS} personas por sala, cada una en su propio dispositivo.` },
  ],

  namePromptTitle: "¿Cómo te llamas?",
  namePromptBody: "Así te verán los demás en el marcador.",
  enterRoom: "Entrar a la sala",

  room: "Sala",
  exit: "Salir",
  cancel: "Cancelar",
  exitTitle: "¿Salir de la sala?",
  exitBody: "Volverás al inicio. Puedes regresar con el enlace de la sala.",
  scoreboard: "Marcador",
  timeLeft: "Tiempo restante",
  you: "tú",
  offline: "ausente",
  points: (n: number) => (n === 1 ? "1 punto" : `${n} puntos`),

  inviteTitle: "Invita a tu equipo",
  copyLink: "Copiar enlace",
  linkCopied: "¡Copiado!",
  linkCopiedToast: "Enlace copiado",
  copyFailed: "Copia este enlace:",
  copyFailedShort: "No se pudo copiar",
  share: "Compartir",
  shareText: "¡Únete a GuessBrawl: a ver quién adivina primero la palabra!",
  close: "Cerrar",
  qrLabel: (code: string) => `Código QR para entrar a la sala ${code}`,

  lobbyTitle: "Cuando quieran",
  lobbyBody: (n: number) => (n === 1 ? "Por ahora solo estás tú. Comparte el enlace, o empieza por tu cuenta." : `${n} personas en la sala.`),
  startRound: "Empezar ronda",
  getReady: "Prepárate",
  wordNumber: (n: number) => `Palabra ${n}`,
  wordLeft: "Tiempo para esta palabra",
  wordTimeUp: "Se acabó el tiempo para esta palabra",
  notAWord: "No está en la lista de palabras",
  tooShort: `Escribe ${WORD_LENGTH} letras`,
  solvedMine: (tries: number) => (tries === 1 ? "¡Resuelta al primer intento!" : `¡Resuelta en ${tries} intentos!`),
  outOfTries: "Sin intentos",
  waitingOthers: "Esperando a los demás…",
  answerWas: (word: string) => `La palabra era ${word}`,
  firstSolved: (name: string) => `${name} la resolvió primero`,
  resultsTitle: "¡Se acabó el tiempo!",
  winner: (name: string) => `Gana ${name}`,
  youWin: "¡Ganaste!",
  tie: "Empate",
  newRound: "Nueva ronda",
  enter: "Enter",
  backspace: "Borrar",
  boardLabel: "Tus intentos",
  rowLabel: (n: number, word: string, result: string) => `Intento ${n}: ${word}, ${result}`,
  markNames: ["no está", "está en otro sitio", "en su sitio"] as const,
  highContrast: "Colores de alto contraste",

  dayHeading: (n: number) => `Palabra del día n.º ${n}`,
  dayWon: "¡Bien hecho!",
  dayLost: "Mañana será",
  shareResult: "Compartir resultado",
  resultCopied: "Resultado copiado",
  nextWord: "Siguiente palabra en",
  stats: "Estadísticas",
  statPlayed: "Días",
  statWinRate: "% resueltas",
  statStreak: "Racha actual",
  statBest: "Mejor racha",
  statDistribution: "Intentos",
  backToStart: "Volver al inicio",

  connecting: "Conectando…",
  reconnecting: "Se perdió la conexión. Reconectando…",
  backHome: "Volver al inicio",
  networkError: "No se pudo contactar con el servidor. Inténtalo de nuevo.",
};

const TABLES: Record<Lang, Strings> = { en, es };

export const STRINGS: Strings = TABLES[LANG];

/** Each language written in itself, for the language switcher. */
export const LANGUAGE_NAMES: Record<Lang, string> = { en: "English", es: "Español" };
